import * as React from 'react'
import {type BaseTeacherAttrs as TeacherT} from '@frontrow/console/src/entities/models/teacher'
import {LanguageDropdown} from '@frontrow/console/src/components/teacher-language-dropdown'
import {TeacherMathSystemSelector} from '@frontrow/console/src/components/teacher-math-system-selector'
import {Alert} from '@frontrow/console/src/components/ui/alert'
import {exhaustive} from '@freckle/exhaustive'

import {SelfContainedTeacherElaStandardSetSelector} from '@frontrow/console/src/components/pages/teachers-page/teacher-information-button-modal/self-contained-teacher-ela-standard-set-selector'
import {dateAccessor, booleanAccessor} from '@frontrow/console/src/helpers/formatters'
import {
  Field,
  FIELD_GRID,
  SUB_HEADING
} from '@frontrow/console/src/components/pages/teachers-page/teacher-information-button-modal/field'
import ErrorAlert from '@frontrow/console/src/components/common/error-alert'

type Props = {
  teacher: TeacherT
  onChange: () => void
}

type ErrorOrSuccessMessageT =
  | {
      tag: 'error'
      message: string
    }
  | {
      tag: 'success'
      message: string
    }
  | {
      tag: 'nothing-to-report'
    }

// Weighted rather than equal columns so the math system's selected option,
// the longest of the three, still reads in full. The selectors' own labels are
// pulled down to the read-only field register so the modal reads as one sheet.
const CURRICULUM_GRID =
  'grid grid-cols-[1.8fr_1.1fr_0.8fr] items-end gap-x-6' +
  ' [&_label]:text-xs [&_label]:font-semibold [&_label]:uppercase' +
  ' [&_label]:tracking-wide [&_label]:text-muted-text [&_label]:mb-0.5'

const SYSTEM_CHANGE_IMPLICATIONS_NOTICE =
  'Since a system has been changed, this teacher may notice some changes in student levels.'

export default function TeacherInformation(props: Props): React.ReactElement {
  const {teacher, onChange} = props

  const {
    id,
    classLinkTeacherId,
    cleverTeacherId,
    renaissanceRPIdentifier,
    createdAt,
    email,
    emailVerified,
    role,
    environment,
    googleOnly,
    mathStandardSetId,
    elaStandardSetId
  } = teacher

  const [selectedLanguage, setLanguage] = React.useState(teacher.language)
  const [selectedElaStandardSetId, setSelectedElaStandardSetId] = React.useState<
    string | undefined | null
  >(elaStandardSetId)
  const [selectedMathStandardSetId, setMathStandardSetId] =
    React.useState<string>(mathStandardSetId)
  const [message, setMessage] = React.useState<ErrorOrSuccessMessageT>({
    tag: 'nothing-to-report'
  })

  const screamsErrorWhen = (descriptionOfWhen: string) => (error: unknown) => {
    console.error(error)
    setMessage({
      tag: 'error',
      message: `Unexpected issue ${descriptionOfWhen}. Please contact engineering.`
    })
  }

  function setsSuccessMessage<TState>(
    fieldName: string,
    setState: (a: TState) => void,
    additionalContext?: string
  ): (a: TState) => void {
    return state => {
      setMessage({
        tag: 'success',
        message: `Successfully updated teacher ${fieldName}. ${additionalContext ?? ''}`
      })
      setState(state)
      onChange()
    }
  }

  const mkMessage = () => {
    switch (message.tag) {
      case 'error':
        return (
          <div className="mb-3">
            <ErrorAlert>{message.message}</ErrorAlert>
          </div>
        )
      case 'success':
        return (
          <div className="mb-3">
            <Alert type="success">{message.message}</Alert>
          </div>
        )
      case 'nothing-to-report':
        return <></>
      default:
        return exhaustive(message as never, 'ErrorOrSuccessMessageT')
    }
  }

  const loadingText = 'Loading...'

  // Note: the isActive and schoolMathSystem fields were removed since they were not being populated
  // with any data.  isActive and schoolMathSystem are not returned by the endpoint
  // being used: CApiHelper.fancyPaths.v2.teachers._1(teacherId),
  // This was discovered when both of these properties were removed from the BaseTeacher attributes model.
  // Currently, only CApiHelper.fancyPaths.v2.teachers.me._() returns that additional metadata.

  return (
    <React.Fragment>
      {mkMessage()}

      <section className="mb-5">
        <h3 className={SUB_HEADING}>Identity</h3>
        <div className={FIELD_GRID}>
          <Field name="ID" value={id} />
          <Field name="Email" value={email} />
          <Field name="Email Verified" value={booleanAccessor(emailVerified)} />
          <Field name="Login via Google" value={booleanAccessor(googleOnly)} />
          <Field name="Role" value={role} />
          <Field name="Environment" value={environment} />
          <Field name="Created At" value={dateAccessor(createdAt)} />
        </div>
      </section>

      <section className="mb-5">
        <h3 className={SUB_HEADING}>Curriculum Settings</h3>
        <div className={CURRICULUM_GRID}>
          {/* Each selector renders its label and control as fragment
              siblings, so each needs a cell of its own. */}
          <div className="min-w-0 [&_select]:w-full">
            <TeacherMathSystemSelector
              id="mathSystemSelector"
              mode={{
                tag: 'retrieve-all-without-dark-testing-and-perform-update',
                onError: (action, error) => screamsErrorWhen(`${action} Math system(s)`)(error),
                teacherId: id
              }}
              selectedMathStandardSetId={selectedMathStandardSetId}
              onChangeSelection={setsSuccessMessage(
                'math system',
                setMathStandardSetId,
                SYSTEM_CHANGE_IMPLICATIONS_NOTICE
              )}
              labels={{
                formLabel: 'Math System',
                loadingText
              }}
            />
          </div>
          <div className="min-w-0 [&_select]:w-full">
            <SelfContainedTeacherElaStandardSetSelector
              id="elaStandardSelector"
              teacherId={id}
              selectedElaStandardSetId={selectedElaStandardSetId!}
              onError={(action, error) => screamsErrorWhen(`${action} ELA Standard Sets`)(error)}
              onChangeSelection={setsSuccessMessage(
                'ela standard set',
                setSelectedElaStandardSetId,
                SYSTEM_CHANGE_IMPLICATIONS_NOTICE
              )}
              labels={{
                formLabel: 'ELA Standard Set',
                unselectOption: 'No ELA Standard Set',
                loadingText
              }}
            />
          </div>
          <div className="min-w-0 [&_select]:w-full">
            <LanguageDropdown
              labels={{
                formLabel: 'Language',
                loadingText
              }}
              updateMode={{
                tag: 'component-performs-update',
                teacherId: id
              }}
              onError={screamsErrorWhen('setting teacher language (or retrieving languages)')}
              selectedLanguage={selectedLanguage}
              onChange={setsSuccessMessage('language', setLanguage)}
            />
          </div>
        </div>
      </section>

      <section>
        <h3 className={SUB_HEADING}>External IDs</h3>
        <div className={FIELD_GRID}>
          <Field name="Renaissance RP ID" value={renaissanceRPIdentifier} />
          <Field name="ClassLink Teacher ID" value={classLinkTeacherId} />
          <Field name="Clever Teacher ID" value={cleverTeacherId} />
        </div>
      </section>
    </React.Fragment>
  )
}
